import { createBrowserRouter, Navigate } from "react-router-dom";
import { LoadingState } from "../components/FeedbackStates";
import { RequireAuthentication, RequireRole } from "../components/AuthenticationBoundary";
import { AppLayout } from "../layout/AppLayout";
import { minimumRoleForRoute, shellRoutes } from "../routes/routeManifest";
import { MbipRouteErrorBoundary } from "./MbipRouteErrorBoundary";

export const router = createBrowserRouter([
  {
    path: "/login",
    errorElement: <MbipRouteErrorBoundary />,
    lazy: async () => {
      const { LoginPage } = await import("../pages/LoginPage");
      return { Component: LoginPage };
    },
  },
  {
    path: "/change-password",
    errorElement: <MbipRouteErrorBoundary />,
    lazy: async () => {
      const { ChangePasswordPage } = await import("../pages/ChangePasswordPage");
      return {
        Component: () => <RequireAuthentication><ChangePasswordPage /></RequireAuthentication>,
      };
    },
  },
  {
    path: "/",
    errorElement: <MbipRouteErrorBoundary />,
    element: <RequireAuthentication><AppLayout /></RequireAuthentication>,
    HydrateFallback: () => <LoadingState title="Loading MBIP React shell" />,
    children: [
      {
        index: true,
        lazy: async () => {
          const { HomePage } = await import("../pages/HomePage");
          return { Component: HomePage };
        },
      },
      ...shellRoutes.map((route) => ({
        path: route.path.slice(1),
        handle: { shellRoute: route },
        lazy: async () => {
          const loaded = await (async () => {
          if (route.kind === "forecast") {
            const { ForecastPage } = await import("../pages/ForecastPage");
            return { Component: ForecastPage };
          }
          if (route.kind === "joybuy-inventory") {
            const { JoybuyInventoryPage } = await import("../pages/JoybuyInventoryPage");
            return { Component: JoybuyInventoryPage };
          }
          if (route.kind === "joybuy-operation") {
            const { JoybuyOperationPage } = await import("../pages/JoybuyOperationPage");
            return { Component: JoybuyOperationPage };
          }
          if (route.kind === "joybuy-traffic") {
            const { JoybuyTrafficPage } = await import("../pages/JoybuyTrafficPage");
            return { Component: JoybuyTrafficPage };
          }
          if (route.kind === "joybuy-data-upload") {
            const { JoybuyDataUploadPage } = await import("../pages/JoybuyDataUploadPage");
            return { Component: JoybuyDataUploadPage };
          }
          if (route.kind === "amazon-inventory") {
            const { AmazonInventoryPage } = await import("../pages/AmazonInventoryPage");
            return { Component: AmazonInventoryPage };
          }
          if (route.kind === "global-sales") {
            const { GlobalSalesPage } = await import("../pages/GlobalSalesPage");
            return { Component: GlobalSalesPage };
          }
          if (route.kind === "action-center") {
            const { ActionCenterPage } = await import("../pages/ActionCenterPage");
            return { Component: ActionCenterPage };
          }
          if (route.kind === "channel-sales") {
            if (route.group === "Amazon") {
              const { AmazonSalesPage } = await import("../pages/AmazonSalesPage");
              return { Component: AmazonSalesPage };
            }
            const { JoybuySalesPage } = await import("../pages/JoybuySalesPage");
            return { Component: JoybuySalesPage };
          }
          if (route.kind === "customer-target-attainment") {
            const { CustomerTargetAttainmentPage } = await import("../pages/CustomerTargetAttainmentPage");
            return { Component: CustomerTargetAttainmentPage };
          }
          if (route.kind === "invoice-reconciliation") {
            const { InvoiceReconciliationPage } = await import("../pages/InvoiceReconciliationPage");
            return { Component: InvoiceReconciliationPage };
          }
          if (route.kind === "operating") {
            const { OperatingPage } = await import("../pages/OperatingPage");
            return { Component: OperatingPage };
          }
          if (route.kind === "amazon-promotion") {
            const { AmazonPromotionPage } = await import("../pages/AmazonPromotionPage");
            return { Component: AmazonPromotionPage };
          }
          if (route.kind === "global-profitability") {
            const { GlobalProfitabilityPage } = await import("../pages/GlobalProfitabilityPage");
            return { Component: GlobalProfitabilityPage };
          }
          if (route.kind === "diagnostic") {
            const { ConnectivityPage } = await import(
              "../pages/ConnectivityPage"
            );
            return { Component: ConnectivityPage };
          }
          if (route.kind === "data-sources") {
            const { DataSourcesPage } = await import("../pages/DataSourcesPage");
            return { Component: DataSourcesPage };
          }
          if (route.kind === "amazon-operation") {
            const { AmazonOperationPage } = await import("../pages/AmazonOperationPage");
            return { Component: AmazonOperationPage };
          }
          if (route.kind === "secondary-sales") {
            const { SecondarySellOutPage } = await import("../pages/SecondarySellOutPage");
            return { Component: SecondarySellOutPage };
          }
          if (route.kind === "data-upload") {
            const { DataUploadPage } = await import("../pages/DataUploadPage");
            return { Component: DataUploadPage };
          }
          if (route.kind === "amazon-data-upload") {
            const { AmazonDataUploadPage } = await import("../pages/AmazonDataUploadPage");
            return { Component: AmazonDataUploadPage };
          }
          if (route.kind === "master-data") {
            const { MasterDataPage } = await import("../pages/MasterDataPage");
            return { Component: MasterDataPage };
          }
          if (route.kind === "users-access") {
            const { UsersAccessPage } = await import("../pages/UsersAccessPage");
            return { Component: UsersAccessPage };
          }
          if (route.kind === "scm-global-inventory") {
            const { ScmGlobalInventoryPage } = await import("../pages/ScmGlobalInventoryPage");
            return { Component: ScmGlobalInventoryPage };
          }
          if (route.kind === "cost") {
            const { CostWorkspacePage } = await import("../pages/CostWorkspacePage");
            return { Component: CostWorkspacePage };
          }
          if (route.kind === "rebate-settlement") {
            const { RebateSettlementPage } = await import("../pages/RebateSettlementPage");
            return { Component: RebateSettlementPage };
          }
          const { JoybuyOrderPlanningPage } = await import("../pages/JoybuyCalculatorPage");
          return { Component: JoybuyOrderPlanningPage };
          })();
          const Page = loaded.Component;
          return {
            Component: () => <RequireRole minimum={minimumRoleForRoute(route)}><Page /></RequireRole>,
          };
        },
      })),
      ...(import.meta.env.DEV ? [{
        path: "__internal/bi-presentation",
        lazy: async () => {
          const { BiPresentationHarnessPage } = await import("../pages/BiPresentationHarnessPage");
          return { Component: BiPresentationHarnessPage };
        },
      }] : []),
      {
        path: "finance/invoice-upload",
        Component: () => <Navigate to="/system/data-import-center?dataset=sales_invoice" replace />,
      },
      {
        path: "joybuy/calculator",
        Component: () => <Navigate to="/joybuy/order-planning" replace />,
      },
      {
        path: "amazon/cpfr",
        Component: () => <Navigate to="/amazon/inventory?tab=cpfr" replace />,
      },
      {
        path: "sales/forecast",
        lazy: async () => {
          const { ForecastPage } = await import("../pages/ForecastPage");
          return { Component: ForecastPage };
        },
      },
      {
        path: "sales/customer-target-attainment",
        lazy: async () => {
          const { CustomerTargetAttainmentPage } = await import("../pages/CustomerTargetAttainmentPage");
          return { Component: CustomerTargetAttainmentPage };
        },
      },
      {
        path: "unauthorized",
        lazy: async () => {
          const { UnauthorizedPage } = await import("../pages/UnauthorizedPage");
          return { Component: UnauthorizedPage };
        },
      },
      {
        path: "*",
        lazy: async () => {
          const { NotFoundPage } = await import("../pages/NotFoundPage");
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
